import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import closeIcon from "../../../../../../assets/icons/closeIcon.svg";
import ProviderRequestStatusFilter from "../../provider-requests/provider-requests-filter-options/status-filter";
import "./provider-profiles-filter-options.scss";

interface ProviderProfilesFilterOptionsProps {
  isOpenFilter: boolean;
  setIsOpenFilter: (open: boolean) => void;
  providerProfilesFilterOptions: any[];
  onApplyFilters: (filters: any) => void;
  activeFilters: any;
}

const ProviderProfilesFilterOptions: React.FC<
  ProviderProfilesFilterOptionsProps
> = ({
  isOpenFilter,
  setIsOpenFilter,
  providerProfilesFilterOptions,
  onApplyFilters,
  activeFilters,
}) => {
  /* -------------------- STATE -------------------- */
  const [draftOptions, setDraftOptions] = useState<any[]>([]);
  const [businessName, setBusinessName] = useState<string>("");
  const [businessType, setBusinessType] = useState<string[]>([]);
  const [businessTypeError, setBusinessTypeError] = useState<string>("");
  const [businessNameError, setBusinessNameError] = useState<string>("");

  /* -------------------- INIT ON OPEN -------------------- */
  useEffect(() => {
    if (!isOpenFilter) return;

    const updatedDraft = providerProfilesFilterOptions.map((option) => {
      switch (option.label) {
        case "Business Name":
          return {
            ...option,
            value: !!activeFilters?.businessName?.length,
          };
        case "Business Type":
          return {
            ...option,
            value: !!activeFilters?.businessType?.length,
          };
        default:
          return option;
      }
    });

    setDraftOptions(updatedDraft);
    setBusinessName(activeFilters?.businessName || "");
    setBusinessType(activeFilters?.businessType || []);
    setBusinessTypeError("");
    setBusinessNameError("");
  }, [isOpenFilter]);

  /* -------------------- CHECKBOX TOGGLE -------------------- */
  const handleToggle = (label: string) => {
    setDraftOptions((prev) =>
      prev.map((opt) =>
        opt.label === label ? { ...opt, value: !opt.value } : opt
      )
    );
    if (label === "Business Name") {
      setBusinessNameError("");
    }

    if (label === "Business Type") {
      setBusinessType([]);
      setBusinessTypeError("");
    }
  };

  /* -------------------- RESET -------------------- */
  const handleReset = () => {
    setDraftOptions(
      providerProfilesFilterOptions.map((opt) => ({
        ...opt,
        value: false,
      }))
    );
    setBusinessName("");
    setBusinessType([]);
    setBusinessTypeError("");
    onApplyFilters({});
  };

  /* -------------------- APPLY -------------------- */
  const handleApply = () => {
    const filters: any = {};
    let hasError = false;

    const isBusinessNameEnabled = draftOptions.find(
      (o) => o.label === "Business Name"
    )?.value;

    const isBusinessTypeEnabled = draftOptions.find(
      (o) => o.label === "Business Type"
    )?.value;

    // Business Name Validation
    if (isBusinessNameEnabled) {
      if (!businessName.trim()) {
        setBusinessNameError("Business Name cannot be empty");
        hasError = true;
      } else {
        filters.businessName = businessName.trim();
      }
    }

    // Business Type Validation
    if (isBusinessTypeEnabled) {
      if (!businessType.length) {
        setBusinessTypeError("Business Type cannot be empty");
        hasError = true;
      } else {
        filters.businessType = businessType;
      }
    }

    if (hasError) return;

    onApplyFilters(filters);
    setIsOpenFilter(false);
  };

  const isAnyFilterSelected = draftOptions.some((opt) => opt.value);

  /* -------------------- RENDER FIELD -------------------- */
  const renderFilterField = (option: any) => {
    switch (option.label) {
      case "Business Name":
        return (
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Business Name"
            value={businessName}
            onChange={(e) => {
              setBusinessName(e.target.value);
              if (e.target.value?.trim()) {
                setBusinessNameError("");
              }
            }}
            error={!!businessNameError}
            helperText={businessNameError}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
              },
            }}
            sx={{
              marginTop: 1,
              backgroundColor: "transparent",
              marginBottom: 1
            }}
          />
        );

      case "Business Type":
        return (
          <ProviderRequestStatusFilter
            value={businessType}
            onChange={setBusinessType}
            isStatusEnabled={option.value}
            error={businessTypeError}
            statusOptions={option.options || []}
          />
        );

      default:
        return null;
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <div className="provider-profiles-filter-options-wrapper">
      <div className="provider-profiles-filter-options-container">
        {/* HEADER */}
        <div className="header-wrapper">
          <div className="provider-profiles-filter-options-container-header">
            <div className="provider-profiles-filter-options-container-header-text">
              Filter
            </div>
            <div className="provider-profiles-filter-options-container-header-icon">
              <img
                src={closeIcon}
                alt="Close"
                onClick={() => setIsOpenFilter(false)}
              />
            </div>
          </div>
        </div>

        {/* FILTER LIST */}
        <div className="provider-profiles-filter-options-container-list">
          {draftOptions.map((option) => (
            <div key={option.label}>
              <label
                htmlFor={option.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  ...(option.value && { marginTop: "4px" }),
                }}
              >
                <Checkbox
                  checked={option.value}
                  onChange={() => handleToggle(option.label)}
                  id={option.label}
                />
                <ListItemText primary={option.label} />
              </label>

              {option.value && renderFilterField(option)}
            </div>
          ))}
        </div>

        {/* ACTION BUTTONS */}
        {isAnyFilterSelected && (
          <div className="provider-profiles-filter-buttons">
            <button className="cancel-btn" onClick={handleReset}>
              Reset
            </button>
            <button className="action-btn" onClick={handleApply}>
              Apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderProfilesFilterOptions;
